/* eslint-disable max-len */
/* eslint-disable i18next/no-literal-string */
import React from 'react';
import { Stack } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { useAppTheme } from 'common';
import { Text } from 'common/components/Text';
import { ArtifactView } from 'features/dashboard/Cards/Features/Common/ArtifactPile/ArtifactView';
import { useLocalizedStrings } from 'i18n';

import { AddIcon, Avatar, IAvatarProps } from './Avatar';
import { useStyles } from './AvatarButton.styles';

export interface IAvatarButtonProps extends IAvatarProps {
  /**
   * Optional aria-label for the button that contains the avatar.
   * There are already default values.
   */
  /**
   * Identifier of the avatar button
   */
  id?: string;
  /**
   * Whether or not the button is selected ("active").
   *
   * @default false
   */
  isSelected?: boolean;
  /**
   * Required click handler.
   */
  onClick: () => void;
  /**
   * Optional focus handler.
   */
  onFocus?: () => void;
}

export const AvatarButton: React.FC<IAvatarButtonProps> = (props: IAvatarButtonProps) => {
  const { isSelected = false, name, id, onClick, onFocus, src, status } = props;
  const { t } = useLocalizedStrings();

  const baseSize = 56;
  const isAddStatus = status === 'add';
  const { palette } = useAppTheme();
  const styles = useStyles(isSelected, baseSize, isAddStatus);
  const addIconClipPathId = useId();

  // Blue bar to indicate a button is selected ("active")
  const bar = <span className={styles.bar} role="presentation" aria-hidden />;

  let ariaLabel: string;

  switch (status) {
    case 'add':
      ariaLabel = t('Button.AddPerson', { name });
      break;
    case 'hasAlerts':
      ariaLabel = t('Button.SelectPersonWithBreaches', { name });
      break;
    default:
      ariaLabel = name;
      break;
  }

  return (
    <Stack verticalAlign="space-between" horizontalAlign="center" className={styles.wrapper}>
      <Stack.Item>
        <ArtifactView
          id={id ?? name}
          hasError={status === 'hasAlerts'}
          title={ariaLabel}
          onClick={onClick}
          onFocus={onFocus}
          imageUrl={src}
          baseSize={baseSize}
          className={styles.artifactWrapper}
          telemetryPrefix="ITP_AvatarButton"
          showTooltip={false}
          zoomOnFocus={false}
        />
        {isAddStatus && <AddIcon className={styles.addIcon} primaryColor={palette.themePrimary} id={addIconClipPathId} />}
      </Stack.Item>
      <Stack className={styles.textWrapper}>
        <Text className={styles.nameLabel} nowrap variant="Caption2">
          {name}
        </Text>
        {isSelected && bar}
      </Stack>
    </Stack>
  );
};
